import { PatientMatchState } from "../matchState";

/**
 * Resolves which patient the Quick Entry Patient row settled on, so Confirm All can link it to the
 * transaction (`patientDataid`).
 *
 * AB#35071: the link used to have exactly ONE channel - PatientInfoRow's write-once effect fires
 * `onSelectPatient(values, candidates[0].patientId)`, the parent stores that in
 * `quickEntryPatientResolution`, and Confirm All reads it. Two reset paths clear that state
 * (transaction change, fresh extraction), the effect is one-shot per resolved identity so it will
 * not re-fire for the same patient, and the guard then dropped the link **silently**: green row,
 * transaction completes, `PatientDataID` NULL, Name and DOB blank forever after. Proven against the
 * database, and reproducing whether the specialist typed the patient in or the AI extracted it.
 *
 * Every hop in that chain reads correctly in isolation, so this does not try to repair one of them.
 * It removes the single point of failure: the id now also travels on `PatientMatchState` (reported
 * on every match-state change rather than once), and this takes the first trustworthy source.
 *
 * The other half of the fix is that "confirmed but unresolvable" is now its own outcome instead of
 * being indistinguishable from "no patient". Silently completing a transaction without the patient
 * the specialist confirmed is the actual defect - losing it quietly is worse than refusing to
 * commit, because nobody finds out until the request is reopened.
 *
 * Never logs a value - patient identity is PHI (.claude/rules/hipaa-compliance.md).
 */
export type ConfirmedPatientId =
    /** Link this id as `patientDataid`. */
    | { status: "resolved"; patientId: number }
    /** Specialist entered a new patient; `createQuickEntryPatient` creates and links it. */
    | { status: "create" }
    /** No patient confirmed and none already linked - completing without one is legitimate. */
    | { status: "none" }
    /** A patient IS confirmed but no id can be found anywhere. Must surface, never be swallowed. */
    | { status: "unresolved" };

type PatientResolution = { mode: "select"; patientId?: number } | { mode: "create" } | undefined;

/** Real patient ids are positive DB identities; 0 / negative / NaN are sentinels, not links. */
function usableId(value: unknown): number | undefined {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : undefined;
}

export function resolveConfirmedPatientId(
    resolution: PatientResolution,
    matchState: PatientMatchState,
    transactionDetails: { patient?: { id?: number } } | undefined,
): ConfirmedPatientId {
    // Create-new owns its own endpoint and links the record it creates.
    if (resolution?.mode === "create") {
        return { status: "create" };
    }

    const fromSelection = resolution?.mode === "select" ? usableId(resolution.patientId) : undefined;
    // The row reports this on EVERY match-state change, so unlike the one-shot callback it survives
    // the resolution being cleared and re-derived.
    const fromMatch = matchState.matched && matchState.confidence === "single"
        ? usableId(matchState.patientId)
        : undefined;
    // A re-save on an already-linked transaction must keep its link rather than drop it.
    const fromTransaction = usableId(transactionDetails?.patient?.id);

    const patientId = fromSelection ?? fromMatch ?? fromTransaction;
    if (patientId !== undefined) {
        return { status: "resolved", patientId };
    }

    // Nothing to link. Distinguish "the row is confirmed, so a link was expected" from "no patient
    // was ever settled on" - only the former is a fault.
    const confirmed = matchState.matched && matchState.confidence === "single";
    return confirmed || resolution?.mode === "select" ? { status: "unresolved" } : { status: "none" };
}

export default resolveConfirmedPatientId;
